import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaLayerGroup } from 'react-icons/fa';
import moment from 'moment';
import useCurationsHook from '../../../api/panel/curations';
import '../../../css/admin/curations.css';

// Global shortcut that surfaces the signed-in user's live (in_process, shown
// as "Published") curations — those they own or collaborate on. Sits next to
// the notification bell; clicking an entry (or the footer) jumps to the
// curations page.
export default function CurationShortcut() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { getActiveCurations } = useCurationsHook({ activeEnabled: true, listEnabled: false });

    const items = getActiveCurations.data?.data || [];
    const count = getActiveCurations.data?.count || items.length;

    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        const onDocClick = (e) => {
            if (!wrapRef.current) { return; }
            if (!wrapRef.current.contains(e.target)) { setOpen(false); }
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, []);

    const goToCurations = () => {
        setOpen(false);
        navigate('/admin/curators');
    };

    // Clicking an entry opens that curation's admin edit page directly
    // (not the public view, not the list).
    const openCurationEdit = (id) => {
        setOpen(false);
        navigate(`/admin/curators/${id}/edit`);
    };

    // Nothing running → keep the top bar clean.
    if (!count) { return null; }

    return (
        <div ref={wrapRef} className="curation-shortcut">
            <button
                type="button"
                className="btn curation-shortcut-btn"
                onClick={() => setOpen((o) => !o)}
                aria-label={t('Active curations')}
                title={t('Active curations')}
            >
                <FaLayerGroup />
                <span className="curation-shortcut-bubble">
                    {count > 99 ? '99+' : count}
                </span>
            </button>

            {open && (
                <div className="card shadow-sm curation-shortcut-popup">
                    <div className="curation-shortcut-popup-header">
                        {t('Active curations')}
                    </div>
                    <ul className="curation-shortcut-list">
                        {items.map((c) => (
                            <li key={c._id}>
                                <button type="button" className="curation-shortcut-item" onClick={() => openCurationEdit(c._id)}>
                                    <span className="curation-shortcut-item-dot" />
                                    <span className="curation-shortcut-item-body">
                                        <span className="curation-shortcut-item-title">{c.title}</span>
                                        {c.startDate && (
                                            <span className="curation-shortcut-item-date">
                                                {moment(c.startDate).format('ll')}
                                            </span>
                                        )}
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>
                    <div className="p-2 border-top text-center">
                        <button type="button" className="btn btn-sm w-100 curation-shortcut-cta" onClick={goToCurations}>
                            {t('Open curations')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
